import { z } from 'zod/v4';
import { countryEnum, currencyEnum, subsidyRegimeEnum, technologyEnum } from '~/schemas/enums';

/* -------------------------------------------------------------------------------------------------
 * Common schemas
 * -----------------------------------------------------------------------------------------------*/

export const dealSideEnum = z.enum(['BUY', 'SELL']);
export const dealStatusEnum = z.enum(['OPEN', 'PART_DELIVERED', 'DELIVERED']);
export const dealTrancheStatusEnum = z.enum(['OPEN', 'PART_DELIVERED', 'DELIVERED']);
export const dealDeliveryMethodEnum = z.enum(['CANCEL', 'TRANSFER', 'ISSUANCE']);
export const dealTrancheVolumeTypeEnum = z.enum(['FIXED', 'VARIABLE']);

/* -------------------------------------------------------------------------------------------------
 * API schemas
 * -----------------------------------------------------------------------------------------------*/

/*  Create Deal Request */

const createDealTrancheSchema = z.object({
  client_reference: z.string(),
  contracted_unit_price: z.string().optional(),
  currency: currencyEnum.optional(),
  delivery_method: dealDeliveryMethodEnum.optional(),
  max_delivery_date: z.iso.date(),
  note: z.string().optional(),
  period_end: z.iso.date(),
  period_start: z.iso.date(),
  quantity: z.number().positive(),
  registry_account_id: z.uuid().optional(),
  // Requirements
  requirement_production_device_id: z.uuid().optional(),
  requirement_certificate_label: z.array(z.string()).optional(),
  requirement_certificate_type: z.string(),
  requirement_country: z.array(countryEnum).optional(),
  requirement_minimum_commercial_operation_date: z.iso.date().optional(),
  requirement_sub_national_region: z.array(z.string()).optional(),
  requirement_subsidy_regime: z.array(subsidyRegimeEnum).optional(),
  requirement_supra_national_region: z.array(z.string()).optional(),
  requirement_technology: z.array(technologyEnum).optional(),
  tags: z.record(z.string(), z.string().nullable()),
  volume_type: dealTrancheVolumeTypeEnum.optional(),
});

export const createDealRequestSchema = z.object({
  client_reference: z.string(),
  counterparty_id: z.uuid(),
  executed_at: z.iso.datetime({ offset: true }),
  side: dealSideEnum,
  tranches: z.array(createDealTrancheSchema),
});

export const createDealResponseSchema = z.object({
  async_operation_id: z.uuid(),
  deal_id: z.uuid(),
});

/*  Deal Options Response */

const dealTagOptionSchema = z.object({
  deal_sides: z.array(dealSideEnum),
  is_mandatory: z.boolean(),
  name: z.string(),
  validation_regex: z.string().nullable(),
});

export const dealOptionsResponseSchema = z.object({
  deal_options: z.object({
    side: z.array(dealSideEnum),
  }),
  deal_tranche_options: z.object({
    currency: z.array(currencyEnum),
    delivery_method: z.array(dealDeliveryMethodEnum),
    requirement_certificate_label: z.array(z.string()),
    requirement_certificate_type: z.array(z.string()),
    requirement_country: z.array(countryEnum),
    requirement_supra_national_region: z.array(z.string()),
    requirement_technology: z.array(technologyEnum),
    tags: z.array(dealTagOptionSchema),
    volume_type: z.array(dealTrancheVolumeTypeEnum),
  }),
});

/*  Update Deal Tranche Request */

/**
 * A field left out keeps its stored value and a nullable field sent as `null`
 * clears it.
 *
 * In future this is likely to become dynamic from an OPTIONS request as the editable fields will change
 * depending on the state of the tranche.
 */
export const updateDealTrancheRequestSchema = z.object({
  contracted_unit_price: z.string().nullable().optional(),
  currency: currencyEnum.nullable().optional(),
  delivery_method: dealDeliveryMethodEnum.nullable().optional(),
  note: z.string().max(8000).nullable().optional(),
  registry_account_id: z.uuid().nullable().optional(),
  volume_type: dealTrancheVolumeTypeEnum.optional(),
});

export const updateDealTrancheResponseSchema = z.object({
  async_operation_id: z.uuid(),
  deal_tranche_id: z.uuid(),
});

/*  Create Spot Deal Request */

export const recogniseInboundTransactionWithSpotDealReconciliationRequestSchema = z.object({
  client_reference: z.string(),
  contracted_unit_price: z.string().optional(),
  counterparty_id: z.uuid(),
  currency: currencyEnum.optional(),
  recognition_note: z.string().nullable().optional(),
  registry_transaction_id: z.uuid(),
  transaction_certificate_volume_ids: z.array(z.string()).nullable(),
});

/* -------------------------------------------------------------------------------------------------
 * Types
 * -----------------------------------------------------------------------------------------------*/

export type DealSide = z.infer<typeof dealSideEnum>;
export type DealStatus = z.infer<typeof dealStatusEnum>;
export type DealTrancheStatus = z.infer<typeof dealTrancheStatusEnum>;
export type DealDeliveryMethod = z.infer<typeof dealDeliveryMethodEnum>;
export type DealTrancheVolumeType = z.infer<typeof dealTrancheVolumeTypeEnum>;
export type CreateDealRequest = z.infer<typeof createDealRequestSchema>;
export type UpdateDealTrancheRequest = z.infer<typeof updateDealTrancheRequestSchema>;
export type UpdateDealTrancheResponse = z.infer<typeof updateDealTrancheResponseSchema>;
export type RecogniseInboundTransactionWithSpotDealReconciliationRequest = z.infer<
  typeof recogniseInboundTransactionWithSpotDealReconciliationRequestSchema
>;

export type DealTagOption = z.infer<typeof dealTagOptionSchema>;
export type DealOptionsResponse = z.infer<typeof dealOptionsResponseSchema>;
export type DealTrancheOptionsDropdownKeys = Extract<
  keyof DealOptionsResponse['deal_tranche_options'],
  'requirement_certificate_label' | 'requirement_supra_national_region' | 'requirement_technology'
>;
